import {
  AspectRatio,
  Box,
  Button,
  Fade,
  HStack,
  IconButton,
  Tooltip,
  useClipboard,
  useDisclosure,
} from '@chakra-ui/react'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsArrowsAngleExpand } from 'react-icons/bs'
import { HiOutlineDocumentDownload } from 'react-icons/hi'
import { MdContentCopy } from 'react-icons/md'
import { removeTrailingSlash } from '../../../../utils/urlHelpers'
import useToast from '../../../hooks/useToast'
import { GenerateImageResponse } from '../../../interfaces'
import { UserContext } from '../../../providers'
import SaveNewFileModal from '../PhotoEditor/SaveNewFileModal'
import ImageWithRetry from './ImageWithRetry'

interface GenerateImagePreviewProps {
  onOpen: () => void
  setCurrentIndex: (index: number) => void
  generatedImages: GenerateImageResponse[]
  index: number
  handleImageLoaded?: (index: number, loaded: boolean) => void
}

const GenerateImagePreview = ({
  onOpen,
  setCurrentIndex,
  generatedImages,
  index,
  handleImageLoaded,
}: GenerateImagePreviewProps) => {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useContext(UserContext)
  const {
    isOpen: isOpenSaveFileModal,
    onOpen: onOpenSaveFileModal,
    onClose: onCloseSaveFileModal,
  } = useDisclosure()
  const { onCopy } = useClipboard(generatedImages[index].url)
  const [isHovered, setIsHovered] = useState<boolean>(false)
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false)

  useEffect(() => {
    handleImageLoaded?.(index, isImageLoaded)
  }, [isImageLoaded, index])

  return (
    <>
      <Box
        position="relative"
        overflow="hidden"
        borderRadius="8px"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <AspectRatio ratio={1} width="100%">
          <ImageWithRetry
            src={generatedImages[index].url}
            setIsImageLoaded={setIsImageLoaded}
          />
        </AspectRatio>
        {isImageLoaded && (
          <>
            <Box
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
              bg="rgba(0, 0, 0, 0.4)"
              borderRadius="inherit"
              opacity={isHovered ? 1 : 0}
              transition="opacity 0.5s ease-in-out"
            />
            <Fade in={isHovered} unmountOnExit>
              <HStack position="absolute" gap="4px" top="8px" right="8px">
                <Tooltip label={t('copy_url', 'Copy URL')}>
                  <IconButton
                    aria-label={t('copy_url', 'Copy URL')}
                    variant="link"
                    icon={<MdContentCopy />}
                    height="24px"
                    minWidth="24px"
                    bg="white"
                    opacity="0.8"
                    borderRadius="4px"
                    _hover={{
                      opacity: 1,
                    }}
                    color="#1a202c"
                    onClick={() => {
                      onCopy()
                      toast({
                        description: t(
                          'copied_url_to_clipboard',
                          'Copied URL to clipboard',
                        ),
                        status: 'success',
                        isClosable: true,
                        duration: 5000,
                        position: 'top-right',
                        variant: 'outline',
                      })
                    }}
                  />
                </Tooltip>
                <Tooltip label={t('download', 'Download')}>
                  <IconButton
                    aria-label={t('download', 'Download')}
                    variant="link"
                    icon={<HiOutlineDocumentDownload />}
                    height="24px"
                    minWidth="24px"
                    bg="white"
                    opacity="0.8"
                    borderRadius="4px"
                    color="#1a202c"
                    _hover={{
                      opacity: 1,
                    }}
                    onClick={() => {
                      const downloadURL = `${removeTrailingSlash(
                        user?.dashboardServerURL ?? '',
                      )}/api/media-library/${
                        user?.clientNumber
                      }/generate-image/${
                        generatedImages[index].imageId
                      }/download?fileName=image.jpg`

                      window.open(downloadURL)
                    }}
                  />
                </Tooltip>
                <Tooltip label={t('expand_image', 'Expand image')}>
                  <IconButton
                    aria-label={t('expand_image', 'Expand image')}
                    variant="link"
                    icon={<BsArrowsAngleExpand />}
                    height="24px"
                    minWidth="24px"
                    bg="white"
                    opacity="0.8"
                    borderRadius="4px"
                    color="#1a202c"
                    _hover={{
                      opacity: 1,
                    }}
                    onClick={() => {
                      setCurrentIndex(index)
                      onOpen()
                    }}
                  />
                </Tooltip>
              </HStack>
              <Button
                position="absolute"
                transform="translate(-50%, -50%)"
                top="50%"
                left="50%"
                onClick={() => {
                  onOpenSaveFileModal()
                }}
              >
                {t('save_to_ml', 'Save to media library')}
              </Button>
            </Fade>
          </>
        )}
      </Box>
      <SaveNewFileModal
        isOpen={isOpenSaveFileModal}
        onClose={onCloseSaveFileModal}
        image={generatedImages[index].url}
      />
    </>
  )
}

export default GenerateImagePreview
